import { useQuery } from '@tanstack/react-query';
import { makeRemoteQueueRequests } from 'main/factories/usecases';
import { GetQueueParams, GetQueueResponse } from '@hone-automation/common';

const getQueueRequests = makeRemoteQueueRequests();

/**
 * Hook to fetch queue requests by view and locationId
 * @param params - Parameters including locationId, optional view filter, requestId, and parentId
 * @returns Query result with GetQueueResponse containing view and requests
 */
export const useGetQueueRequestsQuery = (params: Partial<GetQueueParams>) => {
  const { locationId, view, requestId, parentId } = params;
  const hasValidLocationId = Boolean(locationId && locationId.trim().length > 0);
  // IMPORTANT: Don't fetch without a view, requestId, or parentId - this would return ALL requests
  const hasValidFilter = Boolean(view || requestId || parentId);

  return useQuery({
    queryKey: ['queueRequests', view, requestId, locationId, parentId],
    queryFn: async (): Promise<GetQueueResponse> => {
      if (!locationId) {
        throw new Error('No locationId available');
      }
      if (!view && !requestId && !parentId) {
        throw new Error('Either view, requestId, or parentId is required');
      }

      const response = await getQueueRequests.get({ locationId, view, requestId, parentId });

      // Handle error response
      if (response && typeof response === 'object' && 'error' in response) {
        throw new Error(String((response as { error: unknown }).error));
      }

      // Backend returns different formats:
      // - With view: { view: 'Client', requests: [...] }
      // - Without view (only parentId): [...requests array...]
      let queueResponse: GetQueueResponse;
      if (Array.isArray(response)) {
        // Response is array of requests — use the requested view so QueueContext
        // view-matching logic doesn't get stuck in a loading state
        queueResponse = {
          view,
          requests: response as any[],
        };
      } else {
        // Response is { view, requests }
        queueResponse = response as GetQueueResponse;
      }

      // Sort requests by updatedAt descending
      const sortedRequests = [...queueResponse.requests].sort((a, b) => {
        return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
      }) as GetQueueResponse['requests'];

      return {
        view: queueResponse.view,
        requests: sortedRequests,
      };
    },
    enabled: hasValidLocationId && hasValidFilter,
  });
};
